import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  CreditCard,
  Check,
  Calendar,
  Clock,
  AlertCircle,
  MapPin,
  Navigation,
  Star,
  Shield,
  Timer,
  X,
  Users,
  Award,
  TrendingUp
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Header } from '../components/Layout/Header'
import { subscriptionService, TrialStatusResponse } from '../services/subscriptionService'
import AnalyticsService from '../services/analyticsService'
import { SUPPORT_EMAIL } from '../constants/contact'
import emailService from '../services/emailService'
import { useLocalization } from '../contexts/LocalizationContext'
import PostbackService from '../services/postbackService'

const SubscriptionV4 = () => {
  const { user, subscription, checkSubscription } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [paymentResult, setPaymentResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    details?: Record<string, string>
  } | null>(null)
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentFormDisplayed, setPaymentFormDisplayed] = useState(false)
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null)
  const [currentTrialSubscriptionId, setCurrentTrialSubscriptionId] = useState<string | null>(null)
  const [currentFormToken, setCurrentFormToken] = useState<string | null>(null)
  const { t, formatPrice, config } = useLocalization()

  useEffect(() => {
    loadSubscriptionInfo()
    loadSystemPayScript()
  }, [])

  const loadSystemPayScript = () => {
    // Charger le script SystemPay avec la bonne langue
    if (!document.querySelector('script[src*="kr-payment-form"]')) {
      const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'

      const script = document.createElement('script')
      script.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js'
      script.setAttribute('kr-public-key', '67361502:publickey_YK1k7LNpmzU6usYlVGXEO4WOMAsezDDdgnongY2ErOFhe')
      script.setAttribute('kr-language', systemPayLanguage)
      document.head.appendChild(script)

      console.log('🌍 SystemPay chargé avec la langue:', systemPayLanguage)

      // Charger le thème Neon
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon-reset.min.css'
      document.head.appendChild(link)

      const neonScript = document.createElement('script')
      neonScript.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon.js'
      document.head.appendChild(neonScript)
    }
  }

  const loadSubscriptionInfo = async () => {
    try {
      setLoading(true)
      await checkSubscription()
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'abonnement:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Début de la création d\'abonnement')

      const result = await subscriptionService.createSubscriptionWithPayment(user.email)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)

        // Afficher le formulaire de paiement SystemPay
        await displayPaymentForm(result.data.formToken)
        setShowPaymentForm(true)

        // Démarrer la surveillance du paiement
        startPaymentPolling(result.data.orderId)

      } else {
        throw new Error(result.message || 'Erreur lors de la création du paiement')
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const displayPaymentForm = async (formToken: string) => {
    try {
      // Attendre que le script SystemPay soit chargé
      await new Promise<void>((resolve) => {
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            setTimeout(checkKR, 100)
          }
        }
        checkKR()
      })

      if (window.KR) {
        const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'
        console.log('🌍 Configuration du formulaire SystemPay avec la langue:', systemPayLanguage)

        await window.KR.setFormConfig({
          formToken: formToken,
          'kr-language': systemPayLanguage
        })

        const container = document.getElementById('payment-container')
        if (container) {
          container.innerHTML = '<div class="kr-smart-form"></div>'
          await window.KR.renderElements('#payment-container')

          // Marquer que le formulaire est affiché
          setPaymentFormDisplayed(true)
        }
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'affichage du formulaire:', error)
      throw error
    }
  }

  const startPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)

            // Envoyer le postback de paiement réussi avec l'event_id
            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              console.log('💰 Paiement validé, envoi du postback avec event_id:', eventId)
              PostbackService.sendPaymentPostback(eventId, result.data.amount / 100)
            } else {
              console.log('⚠️ Pas d\'event_id trouvé, postback ignoré')
            }

            setPaymentResult({
              type: 'success',
              title: t('subscriptionV3.paymentSuccessTitle'),
              message: t('subscriptionV3.paymentSuccessMessage'),
              details: {
                [t('subscriptionV3.price')]: formatPrice(result.data.amount / 100),
                [t('landing.pricing.planName')]: t('landing.pricing.planName'),
                [t('common.monthly')]: t('common.monthly')
              }
            })

            // Recharger les informations d'abonnement
            await loadSubscriptionInfo()
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)

          } else if (status === 'failed') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentFailedTitle'),
              message: t('subscriptionV3.paymentFailedMessage')
            })

          } else if (status === 'cancelled') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentCancelledTitle'),
              message: t('subscriptionV3.paymentCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors de la vérification du statut:', error)
      }
    }, 2000)

    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleStartTrialV3 = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Starting trial subscription V3 for:', user.email)

      // Tracker le début de l'essai V3
      AnalyticsService.trackEvent('begin_checkout', {
        event_category: 'subscription',
        event_label: 'trial_v3_start',
        currency: config?.currency || 'USD',
        value: 0, // FREE - No charges in V3
      })

      const result = await subscriptionService.createTrialSubscriptionV3(user.email)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        setCurrentTrialSubscriptionId(result.data.subscriptionId)
        setCurrentFormToken(result.data.formToken)

        // Show payment form section
        setShowPaymentForm(true)

        // Display payment form directly
        console.log('🔧 Displaying payment form directly with token:', result.data.formToken)
        await displayPaymentForm(result.data.formToken)

        // Start payment polling (same as V1)
        startTrialPaymentPolling(result.data.orderId)

      } else {
        throw new Error(result.message || 'Error creating trial subscription V3')
      }

    } catch (error) {
      console.error('❌ Error:', error)

      // Tracker l'erreur de création d'essai V3
      AnalyticsService.trackError('trial_v3_creation_error', error instanceof Error ? error.message : 'Unknown trial V3 creation error')

      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const startTrialPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)

            // Envoyer le postback de paiement réussi pour l'essai avec l'event_id
            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              console.log('💰 Essai validé, envoi du postback avec event_id:', eventId)
              PostbackService.sendPaymentPostback(eventId, 0)
            } else {
              console.log('⚠️ Pas d\'event_id trouvé, postback ignoré')
            }

            // Tracker le succès de l'abonnement d'essai V3
            AnalyticsService.trackSubscription('trial_v3')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v3_success',
              currency: config?.currency || 'USD',
              value: 0, // FREE
            })

            // Envoyer l'email de bienvenue d'essai de manière asynchrone
            if (user?.email) {
              console.log('📧 Envoi de l\'email de bienvenue d\'essai pour:', user.email)
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => {
                  console.warn('⚠️ Erreur lors de l\'envoi de l\'email de bienvenue (non bloquant):', error)
                })
            }

            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message'),
              details: {
                [t('subscription.trial.duration')]: t('subscription.trial.duration'),
                [t('subscription.trial.cost')]: t('subscription.trial.cost'),
                [t('subscription.trial.after')]: formatPrice() + t('common.perMonth')
              }
            })

            // Reload subscription info
            await loadSubscriptionInfo()
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)

          } else if (status === 'failed') {
            clearInterval(pollInterval)

            // Tracker l'échec du paiement
            AnalyticsService.trackError('payment_failed', 'Trial V3 payment method registration failed')

            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationFailedTitle'),
              message: t('subscriptionV3.paymentRegistrationFailedMessage')
            })

          } else if (status === 'cancelled') {
            clearInterval(pollInterval)

            // Tracker l'annulation du paiement
            AnalyticsService.trackEvent('payment_cancelled', {
              event_category: 'subscription',
              event_label: 'trial_v3_payment_cancelled',
            })

            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationCancelledTitle'),
              message: t('subscriptionV3.paymentRegistrationCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error)
      }
    }, 2000)

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleCancelSubscription = async () => {
    if (!subscription || !user?.email) return

    const subscriptionType = subscription.status === 'trial' ? 'trial' : 'subscription'
    const confirmMessage = subscription.status === 'trial'
      ? t('subscriptionV3.confirmCancelTrial')
      : t('subscriptionV3.confirmCancelSubscription')

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setCancelLoading(true)
      console.log('🚫 Début de l\'annulation pour:', user.email)

      // Utiliser la nouvelle méthode qui récupère automatiquement l'ID
      const result = await subscriptionService.cancelSubscription(user.email, true)

      if (result.success) {
        // Tracker l'annulation d'abonnement V3
        AnalyticsService.trackEvent('subscription_cancelled', {
          event_category: 'subscription',
          event_label: subscription.status === 'trial' ? 'trial_v3_cancelled' : 'subscription_v3_cancelled',
        })

        // Envoyer l'email d'annulation de manière asynchrone
        if (user?.email) {
          console.log('📧 Envoi de l\'email d\'annulation pour:', user.email, 'Type:', subscriptionType)
          emailService.sendCancellationEmailAsync(
            user.email,
            subscriptionType as 'trial' | 'subscription',
            user.email.split('@')[0],
            subscription.expiryDate
          ).catch(error => {
            console.warn('⚠️ Erreur lors de l\'envoi de l\'email d\'annulation (non bloquant):', error)
          })
        }

        const successMessage = subscription.status === 'trial'
          ? t('subscriptionV3.cancelSuccessTrial')
          : t('subscriptionV3.cancelSuccessSubscription')

        alert(successMessage)

        // Recharger les informations d'abonnement
        await loadSubscriptionInfo()

        // Rediriger vers la page d'accueil après 2 secondes
        setTimeout(() => {
          navigate('/')
        }, 2000)

      } else {
        throw new Error(result.message || t('errors.generic'))
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'annulation:', error)

      const errorMessage = error instanceof Error ? error.message : t('errors.generic')
      alert(`${t('errors.generic')}: ${errorMessage}`)

      // Si l'erreur indique qu'aucun abonnement n'a été trouvé, recharger les infos
      if (errorMessage.includes('Aucun abonnement trouvé') || errorMessage.includes('No subscription found')) {
        console.log('🔄 Rechargement des informations d\'abonnement après erreur')
        await loadSubscriptionInfo()
      }

    } finally {
      setCancelLoading(false)
    }
  }

  const getStatusInfo = () => {
    if (!subscription) return { status: 'none', message: '', color: 'gray' }

    if (subscription.status === 'active') {
      return {
        status: 'active',
        message: 'Active',
        color: 'green'
      }
    }

    if (subscription.status === 'trial') {
      return {
        status: 'trial',
        message: t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext }),
        color: 'blue'
      }
    }

    if (subscription.status === 'cancelled') {
      return {
        status: 'cancelled',
        message: `${t('common.cancelled')} - ${t('subscriptionV3.accessRemains')} ${subscription.expiryDate}`,
        color: 'orange'
      }
    }

    return {
      status: subscription.status,
      message: subscription.status,
      color: 'gray'
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <Header />

      {/* Main content - Nouveau design révolutionnaire */}
      <div className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-green-400 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-purple-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 py-6 sm:py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Affichage de l'abonnement d'essai actif - Nouveau design */}
            {subscription && subscription.status === 'trial' && (
              <div className="mb-8 sm:mb-12">
                <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
                  <div className="relative p-6 sm:p-8 lg:p-12">
                    {/* Background decorative */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>

                    <div className="relative z-10">
                      <div className="flex items-center justify-center mb-6 sm:mb-8">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 sm:p-6">
                          <Timer className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                        </div>
                      </div>

                      <div className="text-center mb-6 sm:mb-8">
                        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                          {t('subscriptionV3.trialActive')}
                        </h2>
                        <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-6 py-3">
                          <span className="text-xl sm:text-2xl font-bold text-white mr-2">
                            {t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext })}
                          </span>
                          <span className="text-white/80 text-sm sm:text-base">
                            {t('subscriptionV3.trialEndsOn')} {subscription.trialEndDate ? new Date(subscription.trialEndDate).toLocaleDateString() : subscription.expiryDate}
                          </span>
                        </div>
                      </div>

                      {/* Trial benefits - Cards design */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-white mx-auto mb-2" />
                          <span className="text-white text-sm sm:text-base font-medium">{t('landing.features.smartMap.title')}</span>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-white mx-auto mb-2" />
                          <span className="text-white text-sm sm:text-base font-medium">{t('landing.features.saveTime.description')}</span>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <Navigation className="w-6 h-6 sm:w-8 sm:h-8 text-white mx-auto mb-2" />
                          <span className="text-white text-sm sm:text-base font-medium">{t('landing.features.navigation.title')}</span>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <Star className="w-6 h-6 sm:w-8 sm:h-8 text-white mx-auto mb-2" />
                          <span className="text-white text-sm sm:text-base font-medium">{t('landing.features.favorites.title')}</span>
                        </div>
                      </div>

                      <div className="text-center">
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancelLoading}
                          className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg"
                        >
                          {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelTrial')}
                        </button>
                        <p className="text-white/70 text-sm mt-3">
                          {t('subscriptionV3.cancelTrialInfo')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section principale - Nouveau design révolutionnaire */}
            {(!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && (
              <div className="space-y-8 sm:space-y-12">

                {/* Hero Section - Ultra moderne */}
                {!showPaymentForm ? (
                  <>
                    <div className="text-center mb-12 sm:mb-16">
                      <div className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6 sm:mb-8 shadow-lg">
                        <span className="animate-pulse mr-2">✨</span>
                        {t('subscriptionV3.noSubscriptionSubtitle')}
                      </div>

                      <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-gray-900 mb-6 sm:mb-8 leading-tight">
                        Trouvez votre
                        <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
                          parking en 30 secondes
                        </span>
                      </h1>

                      <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed">
                        Plus de tours inutiles, plus d'amendes, plus de stress.
                        Notre IA trouve votre place idéale instantanément.
                      </p>

                      {/* Stats Cards - Nouveau design */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto mb-8 sm:mb-12">
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                          <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-xl mb-4 mx-auto">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">15-20 min</div>
                          <div className="text-sm text-gray-600">Économisés par trajet</div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-4 mx-auto">
                            <Users className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">12,847</div>
                          <div className="text-sm text-gray-600">Utilisateurs actifs</div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                          <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl mb-4 mx-auto">
                            <Award className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">98%</div>
                          <div className="text-sm text-gray-600">Taux de satisfaction</div>
                        </div>
                      </div>

                      {/* CTA Principal - Ultra visible */}
                      <div className="text-center mb-12 sm:mb-16">
                        <button
                          onClick={handleStartTrialV3}
                          disabled={paymentLoading}
                          className="group relative bg-gradient-to-r from-green-600 via-green-700 to-emerald-700 text-white text-xl sm:text-2xl font-bold py-5 sm:py-6 px-8 sm:px-12 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:transform-none"
                        >
                          <span className="relative z-10 flex items-center justify-center">
                            {paymentLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                                {t('subscriptionV3.processing')}
                              </>
                            ) : (
                              <>
                                <span className="mr-3">🚀</span>
                                {t('common.continue')}
                              </>
                            )}
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>

                        <p className="text-gray-500 text-sm sm:text-base mt-4">
                          {t('subscriptionV3.noSubscriptionSubtitle')} • Annulation instantanée
                        </p>
                      </div>
                    </div>

                    {/* Testimonials Section - Nouveau design immersif */}
                    <div className="mb-12 sm:mb-16">
                      <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                          Ils ont révolutionné leur quotidien
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                          Découvrez comment GoParkingNow transforme l'expérience de parking
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                        {/* Testimonial 1 - Grande card */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-6 sm:p-8 shadow-xl border border-blue-200 relative overflow-hidden">
                          <div className="absolute top-4 right-4 text-6xl opacity-20">"</div>
                          <div className="relative z-10">
                            <div className="flex items-center mb-4">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                              ))}
                            </div>
                            <blockquote className="text-lg sm:text-xl font-medium text-gray-900 mb-6 leading-relaxed">
                              "Depuis que j'utilise GoParkingNow, je ne perds plus jamais de temps à chercher une place.
                              L'app trouve instantanément les parkings disponibles et me guide parfaitement. J'ai économisé
                              plus de 200€ en amendes cette année !"
                            </blockquote>
                            <div className="flex items-center">
                              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                                M
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">Marie Dubois</div>
                                <div className="text-sm text-gray-600">Commerçante • Paris 15ème</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Testimonial 2 - Grande card */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl p-6 sm:p-8 shadow-xl border border-green-200 relative overflow-hidden">
                          <div className="absolute top-4 right-4 text-6xl opacity-20">"</div>
                          <div className="relative z-10">
                            <div className="flex items-center mb-4">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                              ))}
                            </div>
                            <blockquote className="text-lg sm:text-xl font-medium text-gray-900 mb-6 leading-relaxed">
                              "Application incroyable ! Fini les tours du quartier à la recherche d'une place.
                              L'IA trouve exactement ce qu'il me faut en quelques secondes. La navigation intégrée
                              me guide directement à l'emplacement. Un game changer !"
                            </blockquote>
                            <div className="flex items-center">
                              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                                P
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">Pierre Martin</div>
                                <div className="text-sm text-gray-600">Conducteur quotidien • Lyon</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Benefits Section - Nouveau design visuel */}
                    <div className="mb-12 sm:mb-16">
                      <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                          Tout ce dont vous avez besoin
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                          Fonctionnalités premium pour une expérience de parking exceptionnelle
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        <div className="group bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
                          <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <MapPin className="w-7 h-7 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            {t('landing.features.smartMap.title')}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            Carte interactive avec données temps réel, prix, et accessibilité.
                            Filtrez selon vos critères pour trouver la place parfaite.
                          </p>
                        </div>

                        <div className="group bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-green-200">
                          <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <Navigation className="w-7 h-7 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            {t('landing.features.navigation.title')}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            Navigation GPS intégrée vous guide directement vers votre place réservée.
                            Plus de stress, plus d'erreurs.
                          </p>
                        </div>

                        <div className="group bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-purple-200 md:col-span-2 lg:col-span-1">
                          <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <Star className="w-7 h-7 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            {t('landing.features.favorites.title')}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            Sauvegardez vos emplacements préférés pour un accès ultra-rapide.
                            Vos spots habituels toujours à portée de main.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Final CTA Section */}
                    <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-black rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
                      <div className="relative z-10">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                          Prêt à révolutionner votre parking ?
                        </h2>
                        <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                          Rejoignez plus de 12,000 utilisateurs qui ont déjà transformé leur quotidien
                        </p>

                        <button
                          onClick={handleStartTrialV3}
                          disabled={paymentLoading}
                          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xl font-bold py-4 px-8 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:transform-none"
                        >
                          {paymentLoading ? t('subscriptionV3.processing') : 'COMMENCER MON ESSAI GRATUIT'}
                        </button>

                        <p className="text-green-400 text-sm mt-4 font-medium">
                          ✅ Aucun frais pendant 24h • Annulation instantanée
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Payment Form Section - Ultra mobile optimisé */
                  <div className="max-w-lg mx-auto px-4">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6">
                      {/* Header compact */}
                      <div className="text-center mb-4 sm:mb-6">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                          <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                        </div>
                        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">
                          {t('subscriptionV3.paymentMethodRegistration')}
                        </h2>
                        <p className="text-gray-600 text-sm sm:text-base leading-tight">
                          {t('subscriptionV3.paymentMethodDescription')}
                        </p>
                      </div>

                      {/* Info garantie - Compact */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                        <div className="flex items-start">
                          <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-green-900 text-sm sm:text-base mb-1">
                              {t('subscriptionV3.noChargeInfo')}
                            </h4>
                            <p className="text-green-700 text-xs sm:text-sm leading-tight">
                              Votre carte ne sera débitée qu'après 24h. Annulation gratuite.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Loading state - Compact */}
                      {paymentLoading && (
                        <div className="text-center py-6 sm:py-8">
                          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-3 sm:border-b-4 border-green-600 mx-auto mb-3 sm:mb-4"></div>
                          <p className="text-gray-600 text-sm sm:text-base">{t('subscriptionV3.loadingPaymentForm')}</p>
                        </div>
                      )}

                      {/* Payment form - Optimisé mobile */}
                      <div id="payment-container" className="min-h-[300px] sm:min-h-[350px] bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                        {/* Le formulaire SystemPay sera injecté ici */}
                      </div>

                      {/* Footer discret */}
                      <div className="text-center mt-4">
                        <p className="text-xs text-gray-500">
                          🔒 Paiement sécurisé par SystemPay
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Résultat du paiement */}
            {paymentResult && (
              <div className={`mb-6 sm:mb-8 p-6 rounded-2xl shadow-lg ${
                paymentResult.type === 'success'
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'
                  : 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200'
              }`}>
                <div className="flex items-start">
                  {paymentResult.type === 'success' ? (
                    <Check className="h-6 w-6 text-green-600 mr-4 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-red-600 mr-4 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <h3 className={`text-lg font-bold mb-2 ${
                      paymentResult.type === 'success' ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {paymentResult.title}
                    </h3>
                    <p className={`text-base leading-relaxed ${
                      paymentResult.type === 'success' ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {paymentResult.message}
                    </p>
                    {paymentResult.details && (
                      <div className="mt-4 bg-white/50 rounded-xl p-4">
                        {Object.entries(paymentResult.details).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-1">
                            <span className="font-medium">{key}:</span>
                            <span className="font-semibold">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Sécurité - Nouveau design */}
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  {t('subscriptionV3.security.title')}
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Sécurité et confidentialité au cœur de notre service
                </p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-8">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">
                      {t('subscriptionV3.security.secureTitle')}
                    </h3>
                    <p className="text-blue-700 text-sm leading-relaxed">
                      {t('subscriptionV3.security.secureDescription')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {t('subscriptionV3.faq.howTrialWorks.question')}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('subscriptionV3.faq.howTrialWorks.answer', { price: formatPrice() })}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {t('subscriptionV3.faq.chargesDuringTrial.question')}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('subscriptionV3.faq.chargesDuringTrial.answer')}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {t('subscriptionV3.faq.afterTrial.question')}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('subscriptionV3.faq.afterTrial.answer', { price: formatPrice() })}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {t('subscriptionV3.faq.needHelp.question')}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('subscriptionV3.faq.needHelp.answer')}{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 hover:text-blue-800 font-medium">
                      {SUPPORT_EMAIL}
                    </a>
                    {' '}{t('subscriptionV3.faq.needHelp.answerEnd')}
                  </p>
                </div>
              </div>
            </div>

            {/* Abonnement actif - Nouveau design */}
            {subscription && subscription.status === 'active' && (
              <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    {t('subscriptionV3.activeSubscription.title')}
                  </h2>
                  <p className="text-gray-600 text-lg">
                    {t('subscriptionV3.activeSubscription.statusDescription')}
                  </p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 mb-8">
                  <div className="flex items-start">
                    <Check className="w-6 h-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-green-900 mb-2">
                        {t('subscriptionV3.activeSubscription.statusTitle')}
                      </h3>
                      <p className="text-green-700 text-sm">
                        {t('subscriptionV3.activeSubscription.statusDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">{t('subscriptionV3.activeSubscription.planDetails')}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscriptionV3.activeSubscription.plan')}:</span>
                        <span className="font-semibold text-gray-900">{t('landing.pricing.planName')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscriptionV3.activeSubscription.price')}:</span>
                        <span className="font-semibold text-gray-900">{formatPrice(subscription.price)}{t('common.perMonth')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscriptionV3.activeSubscription.status')}:</span>
                        <span className="font-semibold text-green-600">{t('subscriptionV3.activeSubscription.active')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">{t('subscriptionV3.activeSubscription.nextBilling')}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscriptionV3.activeSubscription.nextPayment')}:</span>
                        <span className="font-semibold text-gray-900">
                          {subscription.nextBilling ? new Date(subscription.nextBilling).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscriptionV3.activeSubscription.daysUntilBilling')}:</span>
                        <span className="font-semibold text-gray-900">{subscription.daysUntilNext || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="bg-red-500 hover:bg-red-600 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg"
                  >
                    {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelSubscription')}
                  </button>
                  <p className="text-gray-500 text-sm mt-3">
                    {t('subscriptionV3.activeSubscription.cancelInfo')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionV4
